import { mat4, vec3 } from "gl-matrix";
import { calcGpsCoords, fusedGpsFromOdom } from "gps-plus-slam-js";
//#region ../src/utils/fused-path.ts
/**
* Fused Path Calculation
*
* Transforms AR odometry positions into GPS coordinates using the alignment matrix.
* This enables displaying the "fused" SLAM+GPS trajectory on the session summary map.
*
* The alignment matrix (computed by the alignment solver in the library) transforms
* AR-local odometry positions into NUE (North-Up-East) meters relative to a
* GPS zero reference point. We then convert NUE to lat/lng for Leaflet.
*
* Internal coordinate convention: X=North, Y=Up, Z=East (right-handed).
*
* @see gps-plus-slam/GpsPlusSlamJs_Docs/docs/2026-01-27-user-feedback.md Issue #4b
*/
/**
* Compute the fused GPS path from odometry positions using the alignment matrix.
*
* Each odometry position is transformed by the alignment matrix to get ENU
* coordinates (meters relative to zeroRef), then converted to GPS lat/lng.
*
* @param input - Odometry positions, alignment matrix, and GPS zero reference
* @returns Array of GPS coordinates suitable for Leaflet polyline
*/
function computeFusedPath(input) {
	const { odometryPositions, alignmentMatrix, zeroRef } = input;
	if (!alignmentMatrix || !zeroRef || odometryPositions.length === 0) return [];
	const matrix = mat4.fromValues(...alignmentMatrix);
	const odomVec = vec3.create();
	const alignedVec = vec3.create();
	return odometryPositions.map((odomPos) => {
		vec3.set(odomVec, odomPos[0], odomPos[1], odomPos[2]);
		vec3.transformMat4(alignedVec, odomVec, matrix);
		const gpsCoord = calcGpsCoords(zeroRef, alignedVec);
		return {
			lat: gpsCoord.lat,
			lng: gpsCoord.lon
		};
	});
}
//#endregion
export { computeFusedPath, fusedGpsFromOdom };
